import {useEffect, useRef, useState} from 'react';
import {ACCOUNT_ID} from '../../api/account';
import {generateAudioPreview} from '../../api/audio';
import {createSubscription, createTopup} from '../../api/payment';
import {getYandexClientId, trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {CLOUDPAYMENTS_PUBLIC_ID, PAYMENT_CURRENCY} from '../../config/constants';
import {VIDEO_EXAMPLES} from '../../config/videoExamples';
import {PERSONAS} from '../../config/personas';

// The one top-up pack a non-subscriber may buy outright (the 3-video pack).
// The 1/5 packs stay a subscriber-only refill, so the paywall hides them until
// there's an active subscription. Identified by id, falling back to size.
const STANDALONE_TOPUP_ID = 'plan_3';
const STANDALONE_TOPUP_VIDEOS = 3;

// Short marketing tagline per plan id (falls back to the plan name).
const PLAN_TAGLINES = {
    plan_1: 'Попробовать сервис',
    plan_3: 'Для семьи и друзей',
    plan_5: 'Поздравить всех коллег',
    plan_10: 'Максимальный пакет',
    sub_weekly_video: 'Новое видео каждую неделю',
    sub_monthly_video: 'Для семьи и друзей',
    sub_monthly_pro: 'Максимум возможностей',
};

// Example result cards, built from the per-persona VIDEO_EXAMPLES config.
const VIDEO_EXAMPLE_CARDS = Object.entries(VIDEO_EXAMPLES).flatMap(([personaId, clips]) => {
    const persona = PERSONAS.find(p => p.id === personaId);
    if (!persona) return [];
    return clips.map(clip => ({
        id: clip.id,
        personaId,
        title: clip.label,
        thumbnail: persona.image.replace(/^\.\//, '/'),
        videoUrl: clip.url,
    }));
});

const PERIOD_LABELS = {
    weekly: '/ нед',
    monthly: '/ мес',
};

const PERIOD_WORDS = {
    weekly: 'в неделю',
    monthly: 'в месяц',
};

// Sentence-form cadence for descriptive copy, keyed by the DB billing_period.
const PERIOD_PHRASES = {
    weekly: 'каждую неделю',
    monthly: 'каждый месяц',
};

const WAVEFORM = [30, 55, 75, 90, 65, 45, 80, 95, 70, 50, 35, 60, 85, 100, 75, 55, 40, 70, 90, 80, 60, 45, 65, 85, 70, 50, 35, 55, 75, 60];

function PlayerCard({title, subtitle, durationLabel, isPlaying, isLoading, onToggle}) {
    return (
        <div className="bg-gray-50 rounded-2xl p-3 flex items-center gap-3 border border-gray-100">
            <button
                onClick={onToggle}
                disabled={isLoading || !onToggle}
                className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center shrink-0 disabled:opacity-60 hover:opacity-90 transition"
            >
                {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"
                                fill="none"/>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                ) : isPlaying ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <rect x="6" y="4" width="4" height="16" rx="1"/>
                        <rect x="14" y="4" width="4" height="16" rx="1"/>
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z"/>
                    </svg>
                )}
            </button>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-brand-dark truncate">{title}</p>
                    {durationLabel && <span className="text-xs text-brand-dark/40 shrink-0">{durationLabel}</span>}
                </div>
                {subtitle && <p className="text-xs text-brand-dark/50 mb-2 truncate">{subtitle}</p>}
                <div className="flex items-center gap-[2px] h-4">
                    {WAVEFORM.map((h, i) => (
                        <div
                            key={i}
                            className={`flex-1 rounded-full transition-colors ${isPlaying ? 'bg-brand-red' : 'bg-brand-red/25'}`}
                            style={{height: `${h}%`}}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Paywall({
                                    isOpen = true,
                                    onClose,
                                    plans = {subscriptions: [], topups: []},
                                    subscription = null,
                                    preSelectedPersona,
                                    preSelectedText,
                                    onPurchaseSuccess,
                                    onClearPreSelection,
                                }) {
    const subscriptionPlans = plans.subscriptions || [];
    const topupPlans = plans.topups || [];

    // The single pack a non-subscriber may buy outright, alongside subscribing.
    const standalonePack = topupPlans.find(p => p.id === STANDALONE_TOPUP_ID)
        || topupPlans.find(p => p.videos === STANDALONE_TOPUP_VIDEOS)
        || null;

    const hasActiveSubscription = !!subscription
        && ['active', 'cancel_at_period_end', 'failed'].includes(subscription.status);

    // Subscribers refill with the full top-up range (1/3/5). Everyone else picks
    // between a subscription and the standalone pack.
    const isSubscriptionMode = !hasActiveSubscription;
    const activePlans = hasActiveSubscription
        ? topupPlans
        : [...subscriptionPlans, ...(standalonePack ? [standalonePack] : [])];
    const recommendedId = hasActiveSubscription
        ? (topupPlans.find(p => p.id === 'plan_3')?.id || topupPlans[topupPlans.length - 1]?.id)
        : (subscriptionPlans[0]?.id || standalonePack?.id);

    // Cadence phrase for the primary subscription, driven by its DB billing_period.
    const subscriptionCadence = PERIOD_PHRASES[subscriptionPlans[0]?.billingPeriod] || '';

    const hasPreSelection = !!preSelectedPersona && !!preSelectedText;

    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [audioPreviewLoading, setAudioPreviewLoading] = useState(false);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
    const [audioPreviewError, setAudioPreviewError] = useState(null);
    const [headerPlaying, setHeaderPlaying] = useState(false);
    const [showYooKassaWidget, setShowYooKassaWidget] = useState(false);
    const [purchaseError, setPurchaseError] = useState(null);
    const [exampleVideo, setExampleVideo] = useState(null);

    const headerAudioRef = useRef(null);
    const yookassaWidgetRef = useRef(null);

    // Keep a valid plan selected for the current mode.
    useEffect(() => {
        if (activePlans.length === 0) return;
        if (activePlans.some(p => p.id === selectedPlanId)) return;
        setSelectedPlanId(recommendedId || activePlans[0].id);
    }, [activePlans, recommendedId, selectedPlanId]);

    // Generate the voice preview for the chosen persona + text.
    useEffect(() => {
        if (!isOpen || !preSelectedText || !preSelectedPersona) {
            setAudioPreviewUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            setAudioPreviewError(null);
            return;
        }
        let cancelled = false;
        let createdUrl = null;
        const fetchPreview = async () => {
            setAudioPreviewLoading(true);
            setAudioPreviewError(null);
            setAudioPreviewUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            try {
                const data = await generateAudioPreview({
                    celebId: preSelectedPersona.id,
                    wishText: preSelectedText,
                });
                if (cancelled) return;
                const audioBlob = new Blob(
                    [Uint8Array.from(atob(data.audio_base64), c => c.charCodeAt(0))],
                    {type: 'audio/mpeg'}
                );
                createdUrl = URL.createObjectURL(audioBlob);
                if (!cancelled) setAudioPreviewUrl(createdUrl);
            } catch (error) {
                if (!cancelled) {
                    console.error('Audio preview error:', error);
                    setAudioPreviewError('Не удалось сгенерировать аудио');
                }
            } finally {
                if (!cancelled) setAudioPreviewLoading(false);
            }
        };
        fetchPreview();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [isOpen, preSelectedText, preSelectedPersona?.id]);

    // Stop any preview audio when the modal closes.
    useEffect(() => {
        if (!isOpen) {
            headerAudioRef.current?.pause();
            setHeaderPlaying(false);
        }
    }, [isOpen]);

    useEffect(() => () => {
        headerAudioRef.current?.pause();
    }, []);

    if (!isOpen) return null;

    // Show every example as a large card, leading with the selected persona's clip.
    const orderedExamples = [
        ...VIDEO_EXAMPLE_CARDS.filter(e => e.personaId === preSelectedPersona?.id),
        ...VIDEO_EXAMPLE_CARDS.filter(e => e.personaId !== preSelectedPersona?.id),
    ];

    const selectedPlan = activePlans.find(p => p.id === selectedPlanId) || null;
    const selectedIsSubscription = selectedPlan?.kind === 'subscription';
    const audioCardTitle = hasPreSelection
        ? `Поздравление от «${preSelectedPersona.name}»`
        : 'Превью поздравления';

    const toggleHeaderPlay = (src) => {
        if (!src) return;
        if (headerPlaying) {
            headerAudioRef.current?.pause();
            setHeaderPlaying(false);
            return;
        }
        if (headerAudioRef.current) headerAudioRef.current.pause();
        const audio = new Audio(src);
        headerAudioRef.current = audio;
        audio.play().catch(() => setHeaderPlaying(false));
        audio.onended = () => setHeaderPlaying(false);
        setHeaderPlaying(true);
    };

    const handleClose = () => {
        headerAudioRef.current?.pause();
        setHeaderPlaying(false);
        onClearPreSelection?.();
        onClose?.();
    };

    const openCloudPaymentsWidget = (orderId, amount, planVideos, planId) => {
        const widget = new window.cp.CloudPayments();
        widget.pay('charge', {
            publicId: CLOUDPAYMENTS_PUBLIC_ID,
            description: `Покупка ${planVideos} видео`,
            amount: amount,
            currency: PAYMENT_CURRENCY,
            invoiceId: orderId,
            accountId: ACCOUNT_ID,
            paymentSchema: 'Single',
            data: {plan_id: planId, credits: planVideos, order_id: orderId}
        }, {
            onSuccess: () => {
                onPurchaseSuccess?.();
                setIsPurchasing(false);
            },
            onFail: () => {
                showError('Оплата не прошла. Попробуйте ещё раз.');
                setIsPurchasing(false);
            },
            onComplete: () => setIsPurchasing(false),
        });
    };

    const openYooKassaWidget = (confirmationToken) => {
        setShowYooKassaWidget(true);
        setTimeout(() => {
            if (yookassaWidgetRef.current) {
                const checkout = new window.YooMoneyCheckoutWidget({
                    confirmation_token: confirmationToken,
                    error_callback: (error) => {
                        console.error('YooKassa error:', error);
                        showError('Ошибка оплаты. Попробуйте ещё раз.');
                        setShowYooKassaWidget(false);
                        setIsPurchasing(false);
                    }
                });
                checkout.on('success', () => {
                    setShowYooKassaWidget(false);
                    onPurchaseSuccess?.();
                    setIsPurchasing(false);
                });
                checkout.on('fail', () => {
                    showError('Оплата не прошла. Попробуйте ещё раз.');
                    setShowYooKassaWidget(false);
                    setIsPurchasing(false);
                });
                checkout.render('yookassa-widget-container');
            }
        }, 100);
    };

    const handleSubscribe = async (planId) => {
        const plan = subscriptionPlans.find(p => p.id === planId);
        if (!plan) return;

        trackEvent('start_subscription');
        setIsPurchasing(true);
        setPurchaseError(null);
        try {
            const yandexClientId = await getYandexClientId();
            const orderData = await createSubscription({
                accountId: ACCOUNT_ID,
                planId: plan.id,
                yandexClientId,
            });
            if (!orderData.confirmation_token) throw new Error('No confirmation token');
            openYooKassaWidget(orderData.confirmation_token);
        } catch (error) {
            console.error('Subscribe error:', error);
            setPurchaseError(error?.data?.detail || 'Ошибка оформления подписки. Попробуйте ещё раз.');
            setIsPurchasing(false);
        }
    };

    const handleTopup = async (planId) => {
        const plan = topupPlans.find(p => p.id === planId);
        if (!plan) return;

        trackEvent('start_topup');
        setIsPurchasing(true);
        setPurchaseError(null);
        try {
            const yandexClientId = await getYandexClientId();
            const orderData = await createTopup({
                accountId: ACCOUNT_ID,
                planId: plan.id,
                yandexClientId,
            });
            if (!orderData.purchase_id) throw new Error('No order ID');

            if (orderData.provider === 'yookassa' && orderData.confirmation_token) {
                openYooKassaWidget(orderData.confirmation_token);
            } else {
                openCloudPaymentsWidget(orderData.purchase_id, orderData.amount_rub, plan.videos, plan.id);
            }
        } catch (error) {
            console.error('Topup error:', error);
            setPurchaseError(error?.data?.detail || 'Ошибка создания заказа. Попробуйте ещё раз.');
            setIsPurchasing(false);
        }
    };

    const handlePurchase = () => {
        if (!selectedPlan) return;
        if (selectedIsSubscription) {
            handleSubscribe(selectedPlan.id);
        } else {
            handleTopup(selectedPlan.id);
        }
    };

    const ctaText = isPurchasing
        ? 'Обработка…'
        : hasPreSelection
            ? 'Купить и начать генерацию'
            : selectedIsSubscription
                ? 'Оформить подписку'
                : `Купить ${selectedPlan?.videos || 1} видео`;

    const leftEyebrow = hasPreSelection ? 'Готово к генерации' : 'Тарифы';
    const leftHeadline = isSubscriptionMode
        ? 'Видео-поздравления от звёзд'
        : 'Не хватило генераций?';

    return (
        <div
            className="fixed inset-0 z-50 flex items-start lg:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
            onClick={handleClose}
        >
            <div
                className="w-full max-w-[1000px] my-6 lg:my-auto flex flex-col lg:flex-row gap-4 lg:items-stretch"
                onClick={e => e.stopPropagation()}
            >
                {/* LEFT — order summary */}
                <div className="lg:w-[360px] lg:shrink-0 bg-white rounded-[24px] p-5 sm:p-6 shadow-2xl flex flex-col">
                    <div className="flex items-start justify-between gap-3 mb-5">
                        <span className="text-sm font-medium text-brand-dark/50">{leftEyebrow}</span>
                        <button
                            onClick={handleClose}
                            aria-label="Закрыть"
                            className="-mt-1 -mr-1 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-brand-dark/60 flex items-center justify-center shrink-0 transition"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>

                    {hasPreSelection ? (
                        <>
                            <div className="flex items-center gap-3 mb-5">
                                <img
                                    src={preSelectedPersona.image}
                                    alt={preSelectedPersona.name}
                                    className="w-14 h-14 rounded-2xl object-cover object-top shrink-0"
                                />
                                <p className="text-xl font-bold text-brand-dark leading-tight">
                                    {preSelectedPersona.name}
                                </p>
                            </div>

                            <p className="text-sm font-medium text-brand-dark/50 mb-2">Текст поздравления:</p>
                            <div className="bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3.5 mb-5">
                                <p className="text-[15px] leading-relaxed text-brand-dark/80 whitespace-pre-line">
                                    «{preSelectedText}»
                                </p>
                            </div>

                            <p className="text-sm font-medium text-brand-dark/50 mb-2">Превью голоса:</p>
                            <PlayerCard
                                title={audioCardTitle}
                                subtitle={
                                    audioPreviewError
                                        ? audioPreviewError
                                        : audioPreviewLoading
                                            ? 'Генерируем аудио превью…'
                                            : 'Превью голоса · готов к разблокировке'
                                }
                                durationLabel="0:20"
                                isPlaying={headerPlaying}
                                isLoading={audioPreviewLoading}
                                onToggle={audioPreviewUrl ? () => toggleHeaderPlay(audioPreviewUrl) : null}
                            />

                            <div className="mt-auto pt-5">
                                <div
                                    className="flex items-center gap-2.5 rounded-2xl bg-brand-red/[0.06] border border-brand-red/15 px-4 py-3">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                         className="text-brand-red shrink-0">
                                        <path d="M13 2L3 14h9l-1 8 10-12h-9z"/>
                                    </svg>
                                    <p className="text-sm font-medium text-brand-dark/70 leading-snug">
                                        Генерация начнётся сразу после оплаты
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="text-2xl font-bold text-brand-dark leading-tight mb-3">
                                {leftHeadline}
                            </h3>
                            <p className="text-[15px] text-brand-dark/60 leading-relaxed mb-5">
                                {isSubscriptionMode
                                    ? `Оформите подписку${subscriptionCadence ? ` с новым видео ${subscriptionCadence}` : ''} или купите пакет из 3 видео без подписки.`
                                    : 'Докупите видео со скидкой за объём — кредиты не сгорают при следующем продлении подписки.'}
                            </p>
                            <div className="mt-auto pt-5">
                                <div
                                    className="flex items-center gap-2.5 rounded-2xl bg-brand-red/[0.06] border border-brand-red/15 px-4 py-3">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                         className="text-brand-red shrink-0">
                                        <path d="M13 2L3 14h9l-1 8 10-12h-9z"/>
                                    </svg>
                                    <p className="text-sm font-medium text-brand-dark/70 leading-snug">
                                        Доступ открывается сразу после оплаты
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* RIGHT — examples + tariffs */}
                <div
                    className="flex-1 min-w-0 bg-[#16121f] rounded-[24px] p-5 sm:p-6 shadow-2xl text-white flex flex-col">
                    <p className="text-lg font-bold mb-3">Примеры результатов</p>
                    <div className="flex gap-3 mb-6 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1 snap-x">
                        {orderedExamples.map(ex => (
                            <button
                                key={ex.id}
                                onClick={() => setExampleVideo(ex)}
                                className="relative w-[108px] shrink-0 aspect-[3/4] rounded-2xl overflow-hidden group snap-start focus:outline-none focus:ring-2 focus:ring-brand-red/50"
                            >
                                <img src={ex.thumbnail} alt={ex.title} loading="lazy"
                                     className="absolute inset-0 w-full h-full object-cover object-top"/>
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <span
                                        className="w-11 h-11 rounded-full bg-white/95 flex items-center justify-center shadow-md group-hover:scale-105 transition">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#16121f">
                                            <path d="M8 5v14l11-7z"/>
                                        </svg>
                                    </span>
                                </span>
                                <span
                                    className="absolute left-2 bottom-2 text-white text-xs font-semibold bg-black/45 rounded-md px-2 py-0.5">
                                    {ex.title}
                                </span>
                            </button>
                        ))}
                    </div>

                    <p className="text-lg font-bold mb-3">Выберите тариф для генерации</p>
                    {activePlans.length === 0 ? (
                        <div
                            className="rounded-2xl bg-white/5 border border-white/10 px-4 py-6 text-center text-white/50 text-sm">
                            Загружаем тарифы…
                        </div>
                    ) : (
                        <div
                            className={`grid gap-3 ${activePlans.length >= 3 ? 'grid-cols-1 sm:grid-cols-3' : activePlans.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                            {activePlans.map(plan => {
                                const isSelected = selectedPlanId === plan.id;
                                const isRecommended = activePlans.length > 1 && plan.id === recommendedId;
                                const tagline = PLAN_TAGLINES[plan.id] || plan.name || '';
                                const periodLabel = plan.billingPeriod ? PERIOD_LABELS[plan.billingPeriod] : '';
                                const periodWord = plan.billingPeriod ? PERIOD_WORDS[plan.billingPeriod] : '';
                                // A weekly sub at videos=1 would read as a bare "1 видео" pack;
                                // append the period so subscriptions stay distinct.
                                const cardTitle = plan.kind === 'subscription' && periodWord
                                    ? `${plan.videos} видео ${periodWord}`
                                    : `${plan.videos} видео сразу`;
                                // Make the billing model explicit so a pack isn't mistaken for a subscription.
                                const paymentNote = plan.kind === 'subscription'
                                    ? 'Подписка, продлевается автоматически'
                                    : 'Разовый платёж, без подписки';
                                return (
                                    <button
                                        key={plan.id}
                                        onClick={() => setSelectedPlanId(plan.id)}
                                        className={`relative text-left rounded-2xl p-4 border-2 transition ${
                                            isSelected
                                                ? 'border-brand-red bg-brand-red/[0.08]'
                                                : 'border-white/10 bg-white/[0.04] hover:border-white/25'
                                        }`}
                                    >
                                        {isRecommended && (
                                            <span
                                                className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-brand-red to-brand-orange text-white text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                                                лучший выбор
                                            </span>
                                        )}
                                        <span
                                            className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                                isSelected ? 'border-brand-red' : 'border-white/30'
                                            }`}
                                        >
                                            {isSelected && <span className="w-2 h-2 rounded-full bg-brand-red"/>}
                                        </span>

                                        <p className="text-lg font-bold leading-tight pr-5">{cardTitle}</p>
                                        <p className="text-xs text-white/55 mt-0.5 mb-3 leading-snug">{tagline}</p>

                                        <div className="flex items-baseline gap-1">
                                            <span className="text-2xl font-bold leading-none">
                                                {plan.price.toLocaleString('ru-RU')}
                                            </span>
                                            <span
                                                className="text-sm text-white/55">₽{periodLabel ? ` ${periodLabel}` : ''}</span>
                                        </div>
                                        {(plan.oldPrice || plan.discount) && (
                                            <div className="mt-1.5 flex items-center gap-2">
                                                {plan.oldPrice && (
                                                    <span className="text-xs text-white/40 line-through">
                                                        {plan.oldPrice.toLocaleString('ru-RU')} ₽
                                                    </span>
                                                )}
                                                {plan.discount && (
                                                    <span
                                                        className="bg-gradient-to-r from-brand-red to-brand-orange text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                                        -{plan.discount}%
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <p className="text-[11px] leading-snug text-white/45 mt-2">{paymentNote}</p>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {isSubscriptionMode && (
                        <div
                            className="mt-4 flex items-start gap-2.5 rounded-2xl bg-white/[0.04] border border-white/10 px-4 py-3">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                 className="text-brand-orange shrink-0 mt-0.5">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4M12 8h.01"/>
                            </svg>
                            <p className="text-sm text-white/65 leading-snug">
                                С подпиской открываются пакеты на 1, 3 и 5 видео по сниженной цене —
                                кредиты не сгорают при продлении.
                            </p>
                        </div>
                    )}

                    {purchaseError && !isPurchasing && (
                        <div
                            className="mt-4 rounded-xl bg-red-500/15 border border-red-400/30 px-4 py-3 text-sm text-red-200">
                            {purchaseError}
                        </div>
                    )}

                    <button
                        onClick={handlePurchase}
                        disabled={isPurchasing || !selectedPlan}
                        className="mt-5 w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold tracking-[-0.16px] hover:opacity-95 transition disabled:opacity-50"
                    >
                        {ctaText}
                    </button>
                    <p className="mt-3 text-center text-xs text-white/45">
                        Безопасная оплата · СБП и карты · возврат 30 дней
                    </p>
                </div>
            </div>

            {exampleVideo && (
                <div
                    className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExampleVideo(null);
                    }}
                >
                    <div className="relative w-full max-w-[420px]" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setExampleVideo(null)}
                            aria-label="Закрыть"
                            className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round"/>
                            </svg>
                        </button>
                        <video
                            src={exampleVideo.videoUrl}
                            controls
                            autoPlay
                            playsInline
                            className="w-full rounded-2xl bg-black aspect-[9/16] object-cover"
                        />
                    </div>
                </div>
            )}

            {showYooKassaWidget && (
                <div
                    className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                        <button
                            onClick={() => {
                                setShowYooKassaWidget(false);
                                setIsPurchasing(false);
                            }}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round"/>
                            </svg>
                        </button>
                        <h3 className="text-xl font-bold text-brand-dark mb-4">Оплата</h3>
                        <div id="yookassa-widget-container" ref={yookassaWidgetRef}></div>
                    </div>
                </div>
            )}
        </div>
    );
}
